// src/oura/delivery/NoPlanYet.tsx
//
// Delivery for a business nothing has been sold to. `GET /delivery` answers
// null until a quotation is activated, and the screen used to say only "No
// contract in Oura yet", which names the state but not the way out. A staffer
// read it as a fault. This says why it is empty and what to do, in order, and
// takes them to the Client tab where the way out lives.
//
// The way out is the Client tab's banner: "Start onboarding" when onboarding is
// on (it reads the signed quote and seeds the plan), "Start a plan" under More
// when it is not. `showOnboarding` is the same switch ClientReviews.tsx reads,
// so this names the control that is actually on screen.

import { RocketIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { showOnboarding } from "../nav";
import { IconSquare, PillButton, Surface, TYPE } from "../ui";
import { OURA_JOINED_SIGNING_ENABLED, SIGN_PRESS } from "../newscan/flags";

export function NoPlanYet({ businessName, onOpenClient }: { businessName: string; onOpenClient: () => void }) {
    const steps: { title: string; detail: string }[] = [
        {
            title: "Open the Client tab",
            detail: "The plan is started from there, not from Delivery.",
        },
        showOnboarding
            ? {
                  title: `Choose ${SIGN_PRESS} in the banner at the top`,
                  detail: OURA_JOINED_SIGNING_ENABLED
                      ? "Use it once the quote is signed, and add the signed quotation. Payment can be recorded later. If onboarding is already running, finish the next step it shows."
                      : "Use it once the quote is signed and paid, and add the signed quotation. If onboarding is already running, finish the next step it shows.",
              }
            : {
                  title: "Open More, then choose Start a plan",
                  detail: "Import the signed quotation and activate it.",
              },
        {
            title: "Come back to Delivery",
            detail: "Every deliverable and its dates appear here, month by month.",
        },
    ];

    return (
        <Surface>
            <div className="flex flex-wrap items-start gap-3">
                <IconSquare icon={RocketIcon} tone="orange" />
                <div className="min-w-0 flex-1 basis-64">
                    <h3 className={cn(TYPE.tt, "m-0 text-gray-900")}>No plan for {businessName} yet</h3>
                    <p className={cn(TYPE.tb, "m-0 mt-1 text-gray-600")}>
                        Delivery lists what we owe this client, month by month. It is built from the signed quotation, and no quotation has been
                        activated for this business.
                    </p>
                </div>
                <PillButton variant="primary" className="max-sm:h-9" onClick={onOpenClient}>
                    Open the Client tab
                </PillButton>
            </div>

            <div className="mt-5 border-t border-gray-100 pt-4">
                <p className={cn(TYPE.lab, "m-0")}>What to do</p>
                <ol className="m-0 mt-3 list-none space-y-3 p-0">
                    {steps.map((s, i) => (
                        <li key={s.title} className="flex gap-3">
                            <span
                                aria-hidden
                                className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-gray-100 text-xs font-semibold tabular-nums text-gray-700"
                            >
                                {i + 1}
                            </span>
                            <div className="min-w-0">
                                <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>{s.title}</p>
                                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{s.detail}</p>
                            </div>
                        </li>
                    ))}
                </ol>
            </div>

            <p className={cn(TYPE.tc, "m-0 mt-5 text-gray-500")}>
                Until then you can still confirm the key pages below. They apply from the next check.
            </p>
        </Surface>
    );
}
